<template>
	<div class="overview-layout">
		<div class="sov-card">
			<div class="tooltip-container">
				<Tooltip position="top" :message="t('tooltips.dashboard_geo.share_of_voice')"/>
			</div>
			<h1 class="title-card">{{ t('dashboard_geo.overview_geo.share_of_voice_title', { aiType: aiTypeName }) }}</h1>
			<div v-if="hasSovDonutData" class="sov-content">
				<div class="sov-chart">
					<apexchart type="donut" height="360" :options="sovChartOptions" :series="sovChartSeries" />
				</div>
				<div class="sov-legend">
					<div v-for="(segment, index) in currentSovSegments" :key="`sov-${segment.key}-${index}`" :class="['sov-legend-item', { 'is-no-delta': !isRangeComparison }]">
						<span class="sov-legend-dot" :style="{ backgroundColor: getSovSegmentColor(segment, index) }"></span>
						<span class="sov-legend-name">{{ segment.name }}</span>
						<span class="sov-legend-value">{{ formatPercent(segment.share) }}</span>
						<span v-if="isRangeComparison" :class="['sov-legend-diff', getDeltaClass(getSovSegmentDelta(segment))]">
							{{ formatDeltaPercent(getSovSegmentDelta(segment)) }}
						</span>
					</div>
				</div>
			</div>
			<p v-else class="no-data">{{ t('no_data') }}</p>
			<div class="sov-actions">
				<button class="show-all-btn" :disabled="!hasSovFullListData" @click="showAllCompetitorsModal = true">{{ t('dashboard_geo.overview_geo.show_all_competitors') }}</button>
			</div>
		</div>

		<div class="scores-grid">
			<div height="153.4px" width="20%" class="card">
				<div class="tooltip-container">
					<Tooltip position="top" :message="t('tooltips.dashboard_geo.score_ai')"/>
				</div>
				<h1 class="title-card">{{t('dashboard_geo.overview_geo.ai_overview_score')}}</h1>
				<p class="score-subtitle">{{ t('dashboard.overview_kpis.ai_overview_triggered') }}: {{ aiOverviewTriggeredCount }}/{{ aiOverviewProcessedCount }}</p>
				<div class="values">
					<div class="graphKPIS">
						<apexchart type="radialBar" height="110%" :key="`ai-overview-${aiOverviewChartSeries[0]}`" :options="aiOverviewChartOptions" :series="aiOverviewChartSeries" />
						<p class="percentage-text">{{ Math.round(aiOverviewVisibilityScore * 100) }}%</p>
						<div v-if="showAiOverviewSparkles" class="sparkle-container">
							<div v-for="(sparkle, i) in aiOverviewSparkles" :key="`ai-overview-${i}`" class="sparkle" :style="sparkle.style"></div>
						</div>
					</div>
				</div>
				<div class="statistics">
					<p>{{ t('visibility.evolution') }}:</p>
					<ComparatorValue style="margin-left: 1rem" :value="evolutionTrend.aiOverview" :comparated="0" />
				</div>
			</div>

			<div height="153.4px" width="20%" class="card">
				<div class="tooltip-container">
					<Tooltip position="left" :message="t('tooltips.dashboard_geo.score_ai')"/>
				</div>
				<h1 class="title-card">{{ t('dashboard_geo.overview_geo.chatgpt_score') }}</h1>
				<div class="values">
					<div class="graphKPIS">
						<apexchart type="radialBar" height="110%" :key="`chatgpt-${chatgptChartSeries[0]}`" :options="chatgptChartOptions" :series="chatgptChartSeries" />
						<p class="percentage-text">{{ Math.round(chatgptVisibilityScore * 100) }}%</p>
						<div v-if="showChatgptSparkles" class="sparkle-container">
							<div v-for="(sparkle, i) in chatgptSparkles" :key="`chatgpt-${i}`" class="sparkle" :style="sparkle.style"></div>
						</div>
					</div>
				</div>
				<div class="statistics">
					<p>{{ t('visibility.evolution') }}:</p>
					<ComparatorValue style="margin-left: 1rem" :value="evolutionTrend.chatgpt" :comparated="0" />
				</div>
			</div>

			<div height="153.4px" width="20%" class="card">
				<div class="tooltip-container">
					<Tooltip position="top" :message="t('tooltips.dashboard_geo.score_ai')"/>
				</div>
				<h1 class="title-card">{{ t('dashboard_geo.overview_geo.ai_mode_score') }}</h1>
				<div class="values">
					<div class="graphKPIS">
						<apexchart type="radialBar" height="110%" :key="`ai-mode-${aiModeChartSeries[0]}`" :options="aiModeChartOptions" :series="aiModeChartSeries" />
						<p class="percentage-text">{{ Math.round(aiModeVisibilityScore * 100) }}%</p>
						<div v-if="showAiModeSparkles" class="sparkle-container">
							<div v-for="(sparkle, i) in aiModeSparkles" :key="`ai-mode-${i}`" class="sparkle" :style="sparkle.style"></div>
						</div>
					</div>
				</div>
				<div class="statistics">
					<p>{{ t('visibility.evolution') }}:</p>
					<ComparatorValue style="margin-left: 1rem" :value="evolutionTrend.aiMode" :comparated="0" />
				</div>
			</div>
			<div height="153.4px" width="20%" class="card">
				<div class="tooltip-container">
					<Tooltip position="left" :message="t('tooltips.dashboard_geo.score_ai')"/>
				</div>
				<h1 class="title-card">{{ t('dashboard_geo.overview_geo.perplexity_score') }}</h1>
				<div class="perplexity-card-content">
					<div class="values">
						<div class="graphKPIS">
							<apexchart type="radialBar" height="110%" :key="`perplexity-${perplexityChartSeries[0]}`" :options="perplexityChartOptions" :series="perplexityChartSeries" />
							<p class="percentage-text">{{ Math.round(perplexityVisibilityScore * 100) }}%</p>
							<div v-if="showPerplexitySparkles" class="sparkle-container">
								<div v-for="(sparkle, i) in perplexitySparkles" :key="`perplexity-${i}`" class="sparkle" :style="sparkle.style"></div>
							</div>
						</div>
					</div>
					<div class="statistics">
						<p>{{ t('visibility.evolution') }}:</p>
						<ComparatorValue style="margin-left: 1rem" :value="evolutionTrend.perplexity" :comparated="0" />
					</div>
					<div v-if="!perplexity_enabled" class="perplexity-lock-overlay">
						<div class="perplexity-lock-message">
							<img :src="lockIcon" alt="" class="perplexity-lock-icon" />
							<span>{{ t('contact_unlock') }}</span>
						</div>
					</div>
				</div>
			</div>
		</div>

		<Modal :showModal="showAllCompetitorsModal" @close-modal="showAllCompetitorsModal = false" width="780px" height="auto">
			<template #title>{{ t('dashboard_geo.overview_geo.all_competitors_title', { aiType: aiTypeName }) }}</template>
			<template #content>
				<div class="sov-modal-table-wrap">
					<table class="sov-modal-table">
						<thead>
							<tr>
								<th>{{ t('dashboard_geo.overview_geo.table.competitor') }}</th>
								<th>{{ t('dashboard_geo.overview_geo.table.mentions') }}</th>
								<th>{{ t('dashboard_geo.overview_geo.table.share_of_voice') }}</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="item in currentSovFullList" :key="`full-${item.key}`">
								<td>{{ item.name }}</td>
								<td>{{ item.mention_count }}</td>
								<td>{{ formatPercent(item.share) }}</td>
							</tr>
						</tbody>
					</table>
					<p v-if="currentSovFullList.length === 0" class="no-data">{{ t('dashboard_geo.overview_geo.no_competitors_found') }}</p>
				</div>
			</template>
		</Modal>
	</div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useDashboardGeoStore } from '@/store/dashboard/dashboardGeo.js';
import { useMarketStore } from '@/store/global/market';
import { useSubNavigationStore } from '@/store/global/subNavigation';
import ComparatorValue from '@/components/dashboard/comparatorValue.vue';
import Modal from '@/components/utilities/modal/modal.vue';
import Tooltip from '@/components/utilities/tooltips.vue';
import lockIcon from '@/assets/icon/lock.svg';
import { useI18n } from 'vue-i18n';

const { t } = useI18n({});

const geoStore = useDashboardGeoStore();
const { scores, last_scores, selectedDate, aiOverviewTriggerStats } = storeToRefs(geoStore);

const marketStore = useMarketStore();
const { perplexity_enabled } = storeToRefs(marketStore);

const subNavigationStore = useSubNavigationStore();
const { selectedAiTypeDashboard, aiTypeOptionsDashboard } = storeToRefs(subNavigationStore);
const showAllCompetitorsModal = ref(false);

const aiTypeName = computed(() => {
	const selected = aiTypeOptionsDashboard.value.find(option => option.id === selectedAiTypeDashboard.value);
	return selected ? selected.name : 'Global';
});

const donutPalette = ['#0EA5A1', '#22C55E', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#84CC16', '#F97316', '#EC4899', '#14B8A6', '#3B82F6'];
const clientColor = '#1D4ED8';
const otherColor = '#B0B5C3';

const hashToIndex = raw => {
	const value = String(raw || '');
	let hash = 0;
	for (let i = 0; i < value.length; i += 1) {
		hash = (hash * 31 + value.charCodeAt(i)) >>> 0;
	}
	return hash % donutPalette.length;
};

const capitalizeCompetitorName = raw => {
	const value = String(raw || '').trim();
	if (!value) {
		return '';
	}

	const cleaned = value
		.replace(/^https?:\/\//i, '')
		.replace(/^www\./i, '')
		.split('/')[0]
		.split('?')[0]
		.split('#')[0]
		.split(':')[0]
		.trim();

	let baseName = cleaned;
	if (cleaned && !cleaned.includes(' ') && cleaned.includes('.')) {
		const parts = cleaned
			.toLowerCase()
			.split('.')
			.map(part => part.trim())
			.filter(Boolean);
		const rootLikeParts = new Set(['co', 'com', 'net', 'org', 'gov', 'edu']);

		if (parts.length >= 3 && parts[parts.length - 1].length === 2 && rootLikeParts.has(parts[parts.length - 2])) {
			baseName = parts[parts.length - 3];
		} else if (parts.length >= 2) {
			baseName = parts[parts.length - 2];
		} else {
			baseName = parts[0] || cleaned;
		}
	}

	return String(baseName || value)
		.split(' ')
		.map(word =>
			word
				.split('-')
				.map(part => {
					if (!part) return part;
					return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
				})
				.join('-'),
		)
		.join(' ');
};

const currentSovSegments = computed(() => {
	const aiKey = selectedAiTypeDashboard.value || 'global';
	const rawSegments = Array.isArray(scores.value?.[aiKey]?.share_of_voice_donut) ? scores.value[aiKey].share_of_voice_donut : [];

	const segments = rawSegments.map(segment => ({
		...segment,
		mention_count: Number(segment?.mention_count || 0),
		share: Number(segment?.share || 0),
		name: capitalizeCompetitorName(segment?.name),
	}));

	const nonOther = segments
		.filter(segment => !segment.is_other)
		.sort((a, b) => {
			if (b.mention_count !== a.mention_count) {
				return b.mention_count - a.mention_count;
			}
			return String(a.name).localeCompare(String(b.name));
		});

	const other = segments.find(segment => segment.is_other);
	return other ? [...nonOther, other] : nonOther;
});

const previousSovSegmentsMap = computed(() => {
	const aiKey = selectedAiTypeDashboard.value || 'global';
	const rows = Array.isArray(last_scores.value?.[aiKey]?.share_of_voice_donut) ? last_scores.value[aiKey].share_of_voice_donut : [];
	const map = new Map();
	rows.forEach(row => {
		map.set(String(row?.key ?? ''), Number(row?.share || 0));
	});
	return map;
});

const isRangeComparison = computed(() => {
	const range = selectedDate.value || [];
	if (range.length < 2 || !range[0] || !range[1]) {
		return false;
	}
	return new Date(range[0]).getTime() !== new Date(range[1]).getTime();
});

const hasSovDonutData = computed(() => {
	return currentSovSegments.value.reduce((sum, segment) => sum + Number(segment.mention_count || 0), 0) > 0;
});

const currentSovFullList = computed(() => {
	const aiKey = selectedAiTypeDashboard.value || 'global';
	const raw = Array.isArray(scores.value?.[aiKey]?.share_of_voice_full_list) ? scores.value[aiKey].share_of_voice_full_list : currentSovSegments.value;

	return raw
		.map(item => ({
			...item,
			name: capitalizeCompetitorName(item?.name),
			mention_count: Number(item?.mention_count || 0),
			share: Number(item?.share || 0),
		}))
		.sort((a, b) => {
			if (b.mention_count !== a.mention_count) {
				return b.mention_count - a.mention_count;
			}
			return String(a.name).localeCompare(String(b.name));
		});
});

const hasSovFullListData = computed(() => currentSovFullList.value.length > 0);

const getSovSegmentColor = (segment, index) => {
	if (segment?.is_other) {
		return otherColor;
	}
	if (segment?.is_client) {
		return clientColor;
	}
	return donutPalette[hashToIndex(segment?.key ?? index)];
};

const formatPercent = share => `${(Number(share || 0) * 100).toFixed(1)}%`;
const normalizedDeltaPercent = deltaShare => Number((Number(deltaShare || 0) * 100).toFixed(1));
const formatDeltaPercent = deltaShare => {
	const value = normalizedDeltaPercent(deltaShare);
	const fixed = Math.abs(value).toFixed(1);
	if (value > 0) return `+${fixed}%`;
	if (value < 0) return `-${fixed}%`;
	return '0.0%';
};
const getDeltaClass = deltaShare => {
	const value = normalizedDeltaPercent(deltaShare);
	if (value > 0) return 'is-positive';
	if (value < 0) return 'is-negative';
	return 'is-neutral';
};
const getSovSegmentDelta = segment => {
	const key = String(segment?.key ?? '');
	const previous = Number(previousSovSegmentsMap.value.get(key) || 0);
	return Number(segment?.share || 0) - previous;
};

const sovChartSeries = computed(() => currentSovSegments.value.map(segment => Number(segment.mention_count || 0)));

const sovChartOptions = computed(() => {
	const segments = currentSovSegments.value;
	const colors = segments.map((segment, index) => getSovSegmentColor(segment, index));
	return {
		chart: {
			type: 'donut',
			toolbar: {
				show: false,
			},
		},
		labels: segments.map(segment => segment.name),
		colors,
		stroke: {
			width: 2,
			colors: ['#fff'],
		},
		dataLabels: {
			enabled: true,
			formatter: value => `${Number(value || 0).toFixed(1)}%`,
			style: {
				fontSize: '12px',
				fontWeight: 700,
			},
			dropShadow: {
				enabled: false,
			},
		},
		legend: {
			show: false,
		},
		tooltip: {
			y: {
				formatter: (value, { seriesIndex }) => {
					const segment = segments[seriesIndex] || {};
					return `${formatPercent(segment.share)} (${segment.mention_count || 0} mentions)`;
				},
			},
		},
		plotOptions: {
			pie: {
				donut: {
					size: '60%',
				},
			},
		},
	};
});

const getColors = value => {
	if (value < 15) return ['var(--rouge-400)'];
	if (value < 40) return ['var(--orange-400)'];
	if (value < 60) return ['var(--vert-100)'];
	return ['var(--vert-400)', 'var(--gris-300)', 'var(--gris-100)'];
};

const seededRandom = seed => {
	const x = Math.sin(seed) * 10000;
	return x - Math.floor(x);
};

const generateSparkles = (count = 12, seed = Date.now()) => {
	const sparkles = [];
	for (let i = 0; i < count; i++) {
		const rand1 = seededRandom(seed + i * 137.5);
		const rand2 = seededRandom(seed + i * 237.8);
		const rand3 = seededRandom(seed + i * 337.2);
		const rand4 = seededRandom(seed + i * 437.6);
		const rand5 = seededRandom(seed + i * 537.9);
		const rand6 = seededRandom(seed + i * 638.1);
		const rand7 = seededRandom(seed + i * 738.4);

		const angle = -180 + rand1 * 180;
		const distance = 55 + rand2 * 15;
		const delay = rand3 * 4000;
		const duration = 1.2 + rand4 * 2.3;
		const size = 0.6 + rand5 * 0.4;
		const offsetX = rand6 * 8 - 4;
		const offsetY = rand7 * 8 - 4;

		sparkles.push({
			style: {
				'--angle': `${angle}deg`,
				'--distance': `${distance}px`,
				'--delay': `${delay}ms`,
				'--duration': `${duration}s`,
				'--size': size,
				'--offset-x': `${offsetX}px`,
				'--offset-y': `${offsetY}px`,
			},
		});
	}
	return sparkles;
};

const aiOverviewTriggeredCount = computed(() => Number(aiOverviewTriggerStats.value?.triggered_count) || 0);
const aiOverviewProcessedCount = computed(() => Number(aiOverviewTriggerStats.value?.processed_count) || 0);

const aiOverviewVisibilityScore = computed(() => scores.value?.ai_overview?.visibility_score ?? 0);
const chatgptVisibilityScore = computed(() => scores.value?.chatgpt?.visibility_score ?? 0);
const aiModeVisibilityScore = computed(() => scores.value?.ai_mode?.visibility_score ?? 0);
const perplexityVisibilityScore = computed(() => scores.value?.perplexity?.visibility_score ?? 0);
const showAiOverviewSparkles = computed(() => Math.round(aiOverviewVisibilityScore.value * 100) >= 60);
const showChatgptSparkles = computed(() => Math.round(chatgptVisibilityScore.value * 100) >= 60);
const showAiModeSparkles = computed(() => Math.round(aiModeVisibilityScore.value * 100) >= 60);
const showPerplexitySparkles = computed(() => Math.round(perplexityVisibilityScore.value * 100) >= 60);

const aiOverviewSparkles = computed(() => {
	if (!showAiOverviewSparkles.value) return [];
	const score = Math.round(aiOverviewVisibilityScore.value * 100);
	const count = 12 + Math.floor((score * 13.7 + score * 7.3) % 7);
	return generateSparkles(count, score * 345.678 + score * 234.567 + score * 123.456);
});

const chatgptSparkles = computed(() => {
	if (!showChatgptSparkles.value) return [];
	const score = Math.round(chatgptVisibilityScore.value * 100);
	const count = 12 + Math.floor((score * 13.7 + score * 7.3) % 7);
	return generateSparkles(count, score * 567.891 + score * 234.567 + score * 890.123);
});

const aiModeSparkles = computed(() => {
	if (!showAiModeSparkles.value) return [];
	const score = Math.round(aiModeVisibilityScore.value * 100);
	const count = 12 + Math.floor((score * 13.7 + score * 7.3) % 7);
	return generateSparkles(count, score * 789.123 + score * 456.789 + score * 12.345);
});

const perplexitySparkles = computed(() => {
	if (!showPerplexitySparkles.value) return [];
	const score = Math.round(perplexityVisibilityScore.value * 100);
	const count = 12 + Math.floor((score * 13.7 + score * 7.3) % 7);
	return generateSparkles(count, score * 891.234 + score * 567.89 + score * 23.456);
});

const evolutionTrend = computed(() => {
	return (
		geoStore.evolutionTrends || {
			chatgpt: 0,
			global: 0,
			overall: 0,
			aiOverview: 0,
			aiMode: 0,
			perplexity: 0,
		}
	);
});

const aiOverviewChartSeries = computed(() => [Math.round(aiOverviewVisibilityScore.value * 100)]);
const chatgptChartSeries = computed(() => [Math.round(chatgptVisibilityScore.value * 100)]);
const aiModeChartSeries = computed(() => [Math.round(aiModeVisibilityScore.value * 100)]);
const perplexityChartSeries = computed(() => [Math.round(perplexityVisibilityScore.value * 100)]);

const buildChartOptions = (seriesRef, label) =>
	computed(() => ({
		chart: {
			type: 'radialBar',
			height: '100%',
			width: '100%',
			animations: {
				enabled: true,
			},
		},
		plotOptions: {
			radialBar: {
				startAngle: -90,
				endAngle: 90,
				hollow: {
					size: '60%',
				},
				dataLabels: {
					name: {
						show: false,
					},
					value: {
						show: false,
					},
				},
			},
		},
		stroke: {
			show: false,
		},
		fill: {
			type: 'solid',
		},
		colors: getColors(seriesRef.value[0]),
		labels: [label],
		tooltip: {
			enabled: false,
		},
		legend: {
			show: false,
		},
		states: {
			hover: {
				filter: {
					type: 'none',
				},
			},
			active: {
				filter: {
					type: 'none',
				},
			},
		},
	}));

const aiOverviewChartOptions = buildChartOptions(aiOverviewChartSeries, 'AI Overview Score');
const chatgptChartOptions = buildChartOptions(chatgptChartSeries, 'ChatGPT Score');
const aiModeChartOptions = buildChartOptions(aiModeChartSeries, 'AI Mode Score');
const perplexityChartOptions = buildChartOptions(perplexityChartSeries, 'Perplexity Score');
</script>

<style lang="scss" scoped>
.overview-layout {
	display: grid;
	grid-template-columns: minmax(380px, 1.25fr) minmax(520px, 1fr);
	gap: 1rem;
	margin-bottom: 1.5rem;
}

.tooltip-container {
	position: absolute;
	top: 10px;
	right: 10px;
}

.sov-card {
	background-color: var(--blanc);
	border-radius: 1rem;
	padding: 1.2rem 2rem 1.2rem 1rem;
	border: 1.5px solid var(--beige-100);
	min-height: 560px;
	display: flex;
	flex-direction: column;
	position: relative;


	.title-card {
		font-size: 1.2rem;
		padding: 0 0.2rem;
	}
	
	.sov-content {
		display: grid;
		grid-template-columns: minmax(360px, 1.35fr) minmax(170px, 0.65fr);
		align-items: center;
		flex: 1;
		margin-top: 0.4rem;
		padding: 0 0.2rem;
	}
	
	.sov-chart {
		display: flex;
		align-items: center;
		justify-content: center;
		overflow: visible;
		padding: 0 0.6rem;
	}
	
	.sov-chart:deep(.apexcharts-canvas),
	.sov-chart:deep(.apexcharts-svg),
	.sov-chart:deep(.apexcharts-inner) {
		overflow: visible !important;
	}
	
	.sov-chart:deep(.apexcharts-graphical) {
		clip-path: none !important;
	}
	
	.sov-legend {
		height: 80%;
		padding: 2rem 0;
		overflow: auto;
		overflow-x: hidden;
		display: grid;
		grid-template-columns: auto minmax(0, min(140px)) auto auto;
		gap: .2rem .4rem;
		align-content: center;
		min-width: 0;
	}
	
	.sov-legend-item {
		display: grid;
		grid-column: 1 / -1;
		grid-template-columns: subgrid;
		align-items: center;
		font-size: 0.9rem;
		min-width: 0;
	}
	
	.sov-legend-dot {
		width: 10px;
		height: 10px;
		border-radius: 50%;
		flex-shrink: 0;
		justify-self: start;
	}
	
	.sov-legend-name {
		min-width: 0;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
		padding: .2rem 0;
		margin-left: -0.2rem;
	}
	
	.sov-legend-value {
		font-weight: 600;
		color: var(--gris-900);
		text-align: right;
		white-space: nowrap;
		justify-self: end;
		padding-right: .5rem;
	}
	
	.sov-legend-diff {
		font-weight: 600;
		justify-self: end;
		text-align: right;
		white-space: nowrap;
	}

	.sov-legend-diff.is-positive {
		color: var(--vert-500);
	}

	.sov-legend-diff.is-negative {
		color: var(--rouge-500);
	}

	.sov-legend-diff.is-neutral {
		color: var(--gris-400);
	}

	.no-data {
		color: var(--gris-400);
		width: 100%;
		height: 100%;
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 1.2rem;
		font-style: italic;
	}

	.sov-actions {
		margin-top: 0.65rem;
		display: flex;
		justify-content: center;
		align-items: center;
	}

	.show-all-btn {
		border: 1px solid var(--gris-200);
		background: var(--blanc);
		color: var(--bleu-500);
		padding: 0.42rem 0.72rem;
		border-radius: 0.55rem;
		font-size: 0.86rem;
		font-weight: 600;
		cursor: pointer;

		&:disabled {
			opacity: 0.45;
			cursor: not-allowed;
		}
	}
}

.sov-modal-table-wrap {
	padding: 0.4rem 1rem 0.9rem 1rem;
	overflow: auto;
}

.sov-modal-table {
	width: 100%;
	border-collapse: collapse;
	font-size: 0.9rem;

	th,
	td {
		padding: 0.52rem 0.35rem;
		border-bottom: 1px solid var(--gris-100);
		text-align: left;
	}

	th:nth-child(2),
	th:nth-child(3),
	td:nth-child(2),
	td:nth-child(3) {
		text-align: right;
		white-space: nowrap;
	}

	td:first-child {
		white-space: normal;
		word-break: break-word;
	}
}

.scores-grid {
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	gap: 1rem;
}

.card {
	background-color: var(--blanc);
	border-radius: 1rem;
	padding: 1.5rem 0.5rem 1rem;
	border: 1.5px solid var(--beige-100);
	min-width: 0;
	flex-grow: 1;
	position: relative;

	.title-card {
		font-size: 1.2rem;
		text-align: flex-start;
		padding: 0 1rem;
	}

	.score-subtitle {
		padding: 0 1rem;
		margin: 0.2rem 0 0;
		font-size: 0.8rem;
		line-height: 1.1;
		color: var(--gris-500);
	}

	.values {
		.graphKPIS {
			margin-top: 1rem;
			position: relative;
			height: 200px;
			width: 100%;
			display: flex;
			justify-content: center;
			align-items: center;
			overflow: hidden;
			min-height: 200px;

			.percentage-text {
				position: absolute;
				top: 36%;
				left: 50%;
				transform: translate(-50%, -50%);
				font-size: 1.7rem !important;
				margin: 0;
				z-index: 0;
				color: var(--bleu-500);
			}

			.mention-text {
				position: absolute;
				top: 25%;
				left: 50%;
				transform: translate(-50%, -50%);
				font-size: 2rem !important;
				color: var(--bleu-500);
			}
		}
	}

	.perplexity-card-content {
		position: relative;
	}

	.perplexity-lock-overlay {
		position: absolute;
		inset: 0;
		backdrop-filter: blur(4px);
		-webkit-backdrop-filter: blur(4px);
		background: rgba(255, 255, 255, 0.2);
		display: flex;
		align-items: center;
		justify-content: center;
		z-index: 5;
	}

	.perplexity-lock-message {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 0.8rem;
		color: var(--gris-500);
		font-weight: 500;
	}

	.perplexity-lock-icon {
		width: 32px;
		height: 32px;
		display: block;
	}

	.statistics {
		display: flex;
		justify-content: center;
		align-items: center;
		color: var(--gris-400);
		margin-top: -4rem;
		font-size: 1rem;

		.small {
			width: 1rem;
			margin: 0 5px 0 5px;
		}
	}
}

.colorRed {
	color: var(--rouge-500);
	fill: var(--rouge-500);
}

.colorGreen {
	color: var(--vert-500);
	fill: var(--vert-500);
}

.sparkle-container {
	position: absolute;
	top: 40%;
	left: 50%;
	width: 0;
	height: 0;
	transform: translate(-50%, -50%);
	z-index: 10;
	pointer-events: none;
}

.sparkle {
	position: absolute;
	width: calc(2.5px * var(--size, 1));
	height: calc(2.5px * var(--size, 1));
	background: radial-gradient(circle, var(--jaune-500) 0%, var(--jaune-400) 40%, var(--jaune-300) 65%, transparent 100%);
	border-radius: 50%;
	transform: translate(calc(-50% + var(--offset-x, 0px)), calc(-50% + var(--offset-y, 0px))) rotate(var(--angle)) translateX(var(--distance)) scale(0);
	opacity: 0;
	animation: sparkleTwinkle var(--duration, 2s) ease-in-out infinite;
	animation-delay: var(--delay);
	box-shadow:
		0 0 4px var(--jaune-400),
		0 0 8px var(--jaune-500);
	filter: brightness(1.3);

	&::before {
		content: '';
		position: absolute;
		top: -2px;
		left: -2px;
		width: calc(6px * var(--size, 1));
		height: calc(6px * var(--size, 1));
		background: radial-gradient(circle, var(--jaune-500) 0%, var(--jaune-400) 45%, transparent 80%);
		border-radius: 50%;
		animation: sparkleGlow 1.2s ease-in-out infinite alternate;
		opacity: 0.8;
		box-shadow: 0 0 6px var(--jaune-400);
	}
}

@media (max-width: 1320px) {
	.overview-layout {
		grid-template-columns: 1fr;
	}

	.sov-card {
		min-height: 420px;
	}
}

@media (max-width: 920px) {
	.scores-grid {
		grid-template-columns: 1fr;
	}

	.sov-card {
		.sov-content {
			grid-template-columns: 1fr;
		}

		.sov-legend {
			max-height: 230px;
		}
	}
}

@keyframes sparkleTwinkle {
	0% {
		transform: translate(calc(-50% + var(--offset-x, 0px)), calc(-50% + var(--offset-y, 0px))) rotate(var(--angle)) translateX(var(--distance)) scale(0) rotate(0deg);
		opacity: 0;
	}
	20% {
		opacity: 0.8;
	}
	50% {
		transform: translate(calc(-50% + var(--offset-x, 0px)), calc(-50% + var(--offset-y, 0px))) rotate(var(--angle)) translateX(var(--distance)) scale(1.2) rotate(180deg);
		opacity: 1;
	}
	80% {
		opacity: 0.8;
	}
	100% {
		transform: translate(calc(-50% + var(--offset-x, 0px)), calc(-50% + var(--offset-y, 0px))) rotate(var(--angle)) translateX(var(--distance)) scale(0) rotate(360deg);
		opacity: 0;
	}
}

@keyframes sparkleGlow {
	0% {
		transform: scale(0.7);
		opacity: 0.5;
	}
	100% {
		transform: scale(1.4);
		opacity: 1;
	}
}
</style>
